<template>
  <div>
    <b-card>
      <div class="organism-template_header card-padding">
        <span class="header__label">Template List</span>
        <div v-if="hasUpdatePermission">
          <atom-button
            buttonId="create-new-template"
            class="header__button"
            buttonStyle="border"
            @click="onClickCreateTemplate"
          >
            <div class="d-flex d-md-none">Create</div>
            <div class="d-none d-md-flex">Create New Template</div>
          </atom-button>
        </div>
      </div>

      <div class="filter-container card-padding pt-0">
        <div class="row mt-0">
          <div class="col-12 col-sm-6 col-lg-6">
            <atom-input
              v-model="filterList.filter"
              title="Filter"
              placeholder="Template name or Description"
            ></atom-input>
          </div>
          <div class="col-12 col-sm-6 col-lg-6 mt-1 mt-sm-0 d-flex">
            <atom-multi-select
              v-model="filterList.tag"
              title="Tags"
              placeholder="All Tags"
              :option="tagOption"
            ></atom-multi-select>
            <div class="d-flex align-items-end">
              <atom-button
                buttonId="button-search"
                iconLeft="SearchIcon"
                @click="clickButtonSearch"
                class="search__button ml-2"
              ></atom-button>
              <div
                class="reset-filter-button ml-1"
                @click="clickButtonClearFilter"
              >
                <span>Reset</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-12">
          <b-table
            sticky-header
            responsive
            :items="items"
            :fields="fields"
            :hover="true"
            @row-clicked="selectRow"
          >
            <template #cell(template)="data">
              <div class="d-flex">
                <div class="header">{{ data.value.name }}</div>

                <div :id="'tag-popover' + data.item.id">
                  <feather-icon
                    icon="TagIcon"
                    size="12"
                    class="tag-icon--style"
                  />
                </div>
                <b-popover
                  ref="popover"
                  :target="'tag-popover' + data.item.id"
                  triggers="hover"
                  placement="right"
                  container="my-container"
                  boundary="window"
                >
                  <template v-slot:title>
                    <div
                      class="d-flex justify-content-between align-items-left"
                    >
                      <span>Tags</span>
                    </div>
                  </template>
                  <div class="badge__container">
                    <b-badge
                      v-for="(tag, index) in data.value.tag"
                      :key="index"
                      variant="light-primary"
                      class="badge--style tag--space"
                    >
                      {{ tag }}
                    </b-badge>
                  </div>
                </b-popover>
              </div>
            </template>
            <template #cell(actionIcon)="data">
              <div class="d-flex justify-content-end">
                <atom-button
                  buttonId="button-compare"
                  buttonStyle="border"
                  @click="clickGenerate(data)"
                  >Generate</atom-button
                >
                <atom-button
                  v-if="hasUpdatePermission"
                  buttonId="button-delete"
                  iconLeft="Trash2Icon"
                  buttonStyle="flat"
                  buttonColor="secondary"
                  class="ml-1"
                  :isDisable="false"
                  @click="clickDeleteTemplate(data)"
                ></atom-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row card-padding">
        <div class="col-6">Showing {{ items.length }} entries</div>
        <div class="col-6 d-flex justify-content-end">
          <b-pagination
            v-model="filterList.currentPage"
            :total-rows="filterList.totalCount"
            :per-page="perPage"
            size="sm"
            class="my-0"
          ></b-pagination>
        </div>
      </div>
    </b-card>
    <footer style="height: 28px"></footer>

    <!-- Modal Start-->
    <section name="modal">
      <molecule-modal-dialog
        modalId="config-template__confirm-delete"
        :isTextCenter="true"
        :textTitle="`Do you want to delete\n“${currentTemplate.name}” ?`"
        buttonTextLeft="Delete"
        buttonColorLeft="danger"
        @clickButtonLeft="clickConfirmDeleteTemplate"
      ></molecule-modal-dialog>
    </section>
    <!-- Modal End-->
  </div>
</template>

<script>
import { BCard, BTable, BPagination, BPopover, BBadge } from "bootstrap-vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomMultiSelect from "@/components/atoms/AtomMultiSelect.vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import { PermissionUser } from "@/constants/permission-user"
import mixinsUser from "@/mixins/mixinsUser"

export default {
  name: "OrganismConfigurationTemplate",
  components: {
    BCard,
    BTable,
    BPagination,
    BPopover,
    BBadge,
    AtomInput,
    AtomMultiSelect,
    AtomButton,
    MoleculeModalDialog,
  },
  mixins: [mixinsUser],
  props: {
    filterList: {
      required: true,
      type: Object,
    },
    tagOption: {
      required: true,
      type: Array,
    },
    fields: {
      type: Array,
      required: false,
      default: [],
    },
    items: {
      type: Array,
      required: false,
      default: [],
    },
  },
  emits: ["clickSearch", "deleteTemplate"],
  data() {
    return {
      modalId: null,

      perPage: 10,

      currentTemplate: {
        id: null,
        name: null,
      },
    }
  },
  computed: {
    hasUpdatePermission() {
      return this.hasPermission(PermissionUser.NCM.CONFIG_TEMPLATE_WRITE)
    },
  },
  watch: {
    "filterList.currentPage": {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit("onUpdateCurrentPage", this.filterList)
        }
      },
    },
  },
  methods: {
    clickButtonSearch() {
      this.$emit("onUpdateFilterData", this.filterList)
    },

    clickButtonClearFilter() {
      this.$emit("onSetDefaultFilterData")
    },

    onClickCreateTemplate() {
      this.$router.push({
        name: "networkConfigurationCreateNewConfigurationTemplate",
      })
    },
    clickDeleteTemplate(data) {
      this.currentTemplate = {
        id: data.item.id,
        name: data.item.template.name,
      }

      this.openModal("config-template__confirm-delete")
    },
    clickConfirmDeleteTemplate() {
      this.$emit("deleteTemplate", this.currentTemplate.id)

      this.closeModal()
    },
    clickGenerate(data) {
      this.$router.push(
        `/network-configuration/configuration-template/use-template/${data.item.id}`
      )
    },
    selectRow(record) {
      this.$router.push(
        `/network-configuration/configuration-template/${record.id}`
      )
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },
    // close modal
    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
.organism-template_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.search__button {
  margin-top: 23px;
  margin-right: 0;
  height: fit-content;
}

.filter-container {
  border-bottom: 1px solid #e9ecef;
}

.header {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #6e6b7b;

  overflow-wrap: break-word;
  word-break: break-word;
}

.sub-header {
  font-weight: 400;
  color: #b9b9c3;
}

.card-body {
  padding: 0;
}

.card-padding {
  padding: 1.5rem;
}

.tag-icon--style {
  color: #9d95f5;
  margin-left: 8px;
  margin-bottom: 3px;
}

.badge__container {
  min-width: 200px;
  width: auto;
}

.badge--style {
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag--space {
  margin-left: 4px;
  margin-bottom: 4px;
}

.value__container {
  display: flex;
}

.reset-filter-button {
  color: #b9b9c3;
  margin-bottom: 8px;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    color: #7367f0;
  }
}

@media screen and (min-width: 576px) {
  .organism-template_header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
